"use client";

/**
 * Fetches the user's current YES/NO position on a given market so PlaceTrade's
 * SellContext + AmountField can cap sell size at owned shares instead of
 * balance. Uses /api/portfolio/positions (already exists in backend).
 *
 * Returns null for each side the user doesn't own. Refreshes on the
 * `positions:refresh` window event fired after a successful trade.
 */

import { useCallback, useEffect, useState } from "react";
import { authFetch } from "@/lib/authFetch";
import type { OwnedPosition } from "../types";

interface RawPosition {
  market_slug?: string;
  marketSlug?: string;
  condition_id?: string;
  conditionId?: string;
  outcome?: string;
  outcome_label?: string;
  shares?: number;
  size?: number;
  avg_price?: number;
  avgPrice?: number;
  current_price?: number;
  currentPrice?: number;
}

function pickShares(p: RawPosition): number {
  return Number(p.shares ?? p.size ?? 0);
}
function pickAvg(p: RawPosition): number {
  return Number(p.avg_price ?? p.avgPrice ?? 0);
}
function pickCurrent(p: RawPosition): number | undefined {
  const v = p.current_price ?? p.currentPrice;
  return typeof v === "number" ? v : undefined;
}

export function useOwnedPositions(
  marketSlug?: string,
  connected?: boolean,
  source: "polymarket" | "kalshi" | "predict" | "hyperliquid" = "polymarket",
  /**
   * Pre-known position the caller already has in scope (e.g. user
   * clicked "Sell" on a portfolio card — the row already knew the
   * shares + avgPrice + outcome). Seeds the initial state so the
   * "You own X @ Y" line in the SellContext renders synchronously
   * with the modal opening, instead of waiting ~300-800ms for the
   * /api/dflow/positions or /api/portfolio/positions roundtrip.
   * The fetch still runs in the background and overwrites this with
   * the authoritative number; the seed is just a fast first paint.
   */
  seed?: { outcome: "yes" | "no"; shares: number; avgPrice: number; currentPrice?: number; displayLabel?: string } | null,
  /**
   * Polymarket condition id when known. The PM positions endpoint keys
   * positions by condition (one condition can serve multiple sub-markets
   * with different slugs — Up/Down brackets, etc.), so matching by slug
   * alone misses freshly-bought positions whose `market_slug` field
   * doesn't equal the modal's `marketSlug` exactly. Matching by conditionId
   * as well catches the case where the modal's slug carries an outcome
   * suffix the position row doesn't have (or vice versa).
   */
  conditionId?: string,
) {
  const [positions, setPositions] = useState<{
    yes?: OwnedPosition | null;
    no?: OwnedPosition | null;
  }>(() => {
    if (seed && seed.shares > 0) {
      const seeded: OwnedPosition = {
        outcome: seed.outcome,
        shares: seed.shares,
        avgPrice: seed.avgPrice,
        currentPrice: seed.currentPrice,
        displayLabel: seed.displayLabel,
      };
      return seed.outcome === "yes"
        ? { yes: seeded, no: null }
        : { yes: null, no: seeded };
    }
    return { yes: null, no: null };
  });

  const fetchOnce = useCallback(async (fresh = false) => {
    if (!marketSlug || !connected) {
      setPositions({ yes: null, no: null });
      return;
    }
    try {
      // Kalshi positions live on the DFlow endpoint (Predexon-primary
      // with DB fallback) — /api/portfolio/positions is Polymarket-only.
      // Filter by ticker so the response is bounded to this market.
      // Hyperliquid uses /api/trading/hyperliquid/positions (per-user,
      // already scoped to the master). The marketSlug there is the
      // outcome-side slug — we match by row.market_slug below.
      // `fresh=1` bypasses the backend's 5s PM positions Redis cache
      // — used after a trade so SELL-Max + cost basis reflect the new
      // shares immediately instead of racing the cache TTL.
      const freshQs = fresh ? "&fresh=1" : "";
      const url = source === "kalshi"
        ? `/api/dflow/positions?ticker=${encodeURIComponent(marketSlug.toUpperCase())}`
        : source === "hyperliquid"
          ? `/api/trading/hyperliquid/positions?status=open`
          : `/api/portfolio/positions?status=open&marketSlug=${encodeURIComponent(marketSlug)}${freshQs}`;
      const r = await authFetch(url);
      if (!r.ok) return;
      const body = await r.json();
      const rows: RawPosition[] = Array.isArray(body?.positions)
        ? body.positions
        : Array.isArray(body?.data)
          ? body.data
          : [];
      const cidLower = conditionId ? conditionId.toLowerCase() : "";
      const forMarket = source === "kalshi"
        // DFlow endpoint already filters by ticker; rows use
        // kalshiTicker, not marketSlug.
        ? rows.filter((p: any) =>
            String(p.kalshiTicker ?? p.marketSlug ?? "").toUpperCase() ===
              marketSlug.toUpperCase(),
          )
        : source === "hyperliquid"
          ? rows.filter((p: any) => String(p.market_slug ?? "") === marketSlug)
          : rows.filter((p) => {
              const pSlug = (p.market_slug ?? p.marketSlug) ?? "";
              if (pSlug === marketSlug) return true;
              const pCid = String(p.condition_id ?? p.conditionId ?? "").toLowerCase();
              if (cidLower && pCid && pCid === cidLower) return true;
              // Slug containment fallback: PM event-style slugs sometimes
              // carry an outcome suffix on one side but not the other.
              if (pSlug && (marketSlug.includes(pSlug) || pSlug.includes(marketSlug))) {
                return true;
              }
              return false;
            });
      const next: { yes?: OwnedPosition | null; no?: OwnedPosition | null } = {
        yes: null,
        no: null,
      };
      for (const p of forMarket) {
        const rawLabel = (p.outcome ?? p.outcome_label ?? "");
        const label = String(rawLabel).toLowerCase();
        // Hyperliquid rows expose `side` 0/1 instead of yes/no labels.
        const isHl = source === "hyperliquid";
        const hlSide = isHl ? Number((p as any).side ?? 0) : null;
        const shares = isHl
          ? Math.abs(Number((p as any).size ?? 0))
          : pickShares(p);
        if (shares <= 0) continue;
        // Map to internal yes/no slot using outcomeIndex when available
        // (authoritative — see polymarket-transform.ts). Fall back to label
        // string matching only when the API didn't provide an index.
        const apiIdx = typeof (p as any).outcomeIndex === "number"
          ? Number((p as any).outcomeIndex)
          : typeof (p as any).outcome_index === "number"
            ? Number((p as any).outcome_index)
            : null;
        const outcome: "yes" | "no" = isHl
          ? hlSide === 1 ? "no" : "yes"
          : apiIdx !== null
            ? (apiIdx === 0 ? "yes" : "no")
            : (label === "no" || label === "down" || label === "under" ? "no" : "yes");
        const pos: OwnedPosition = {
          outcome,
          shares,
          avgPrice: isHl ? Number((p as any).entry_price ?? 0) : pickAvg(p),
          currentPrice: isHl ? Number((p as any).current_price ?? 0) || undefined : pickCurrent(p),
          // Real label from the API ("Over", "Up", "Lakers"). UI uses this
          // for the sell pill instead of forcing "YES"/"NO".
          displayLabel: rawLabel ? String(rawLabel) : undefined,
        };
        if (pos.outcome === "yes") next.yes = pos;
        else next.no = pos;
      }
      setPositions(next);
    } catch {
      // Silent — sell flow just won't pre-fill max; user can still type shares.
    }
  }, [marketSlug, connected, source, conditionId]);

  useEffect(() => {
    fetchOnce();
    // Refresh handler fires `fresh=true` so the backend skips its 5s
    // PM positions cache. Without this, two rapid BUYs leave the second
    // fill invisible to the SELL-Max calculator and the user sells the
    // pre-trade share count. Retry at +1.5s + +4s catches the PM
    // data-api indexing window.
    const refreshTimers = new Set<ReturnType<typeof setTimeout>>();
    const handler = () => {
      fetchOnce(true);
      // PM data-api takes 5-15s to surface a freshly-mined trade. Three
      // tight retries (immediate + 1.5s + 4s) caught most cases but missed
      // slow-indexing windows — symptom: SellContext said "you don't own
      // any YES shares" while the MyPositions sidebar already showed the
      // new row. Extending to 8s + 15s covers PM's worst observed lag.
      [1500, 4000, 8000, 15000].forEach((delay) => {
        const id = setTimeout(() => {
          refreshTimers.delete(id);
          fetchOnce(true);
        }, delay);
        refreshTimers.add(id);
      });
    };
    if (typeof window !== "undefined") {
      window.addEventListener("positions:refresh", handler);
      window.addEventListener("portfolio:refresh", handler);
    }
    return () => {
      refreshTimers.forEach((id) => clearTimeout(id));
      if (typeof window !== "undefined") {
        window.removeEventListener("positions:refresh", handler);
        window.removeEventListener("portfolio:refresh", handler);
      }
    };
  }, [fetchOnce]);

  return positions;
}
